// Small marketing layout primitives shared by the public pages. They
// encode the page's section rhythm (vertical padding, max width) and the
// eyebrow + heading pattern so pages stay declarative and consistent.

import type { ReactNode } from "react";

// Mono uppercase kicker above a section heading.
export function Eyebrow({
  children,
  tone = "accent",
}: {
  children: ReactNode;
  tone?: "accent" | "danger";
}) {
  const color = tone === "danger" ? "text-danger" : "text-accent";
  return (
    <div
      className={`font-mono text-[11px] ${color} uppercase tracking-[0.2em] mb-3`}
    >
      {children}
    </div>
  );
}

// Section wrapper. `alt` paints the alternating darker band; `width`
// caps the inner container.
export function Section({
  children,
  alt = false,
  width = "1100px",
  id,
}: {
  children: ReactNode;
  alt?: boolean;
  width?: string;
  id?: string;
}) {
  return (
    <section
      id={id}
      className="py-12 sm:py-20 px-4 sm:px-6"
      style={alt ? { background: "rgb(var(--color-bg))" } : undefined}
    >
      <div className="mx-auto" style={{ maxWidth: width }}>
        {children}
      </div>
    </section>
  );
}

// Centered eyebrow + heading + optional lead, the standard section intro.
export function SectionHeading({
  eyebrow,
  title,
  lead,
  tone = "accent",
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  tone?: "accent" | "danger";
}) {
  return (
    <div className="text-center mb-10 sm:mb-12">
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <h2 className="font-display text-[26px] sm:text-[36px] font-bold text-content mb-3 leading-[1.2]">
        {title}
      </h2>
      {lead && (
        <p className="font-sans text-[14px] sm:text-[15px] text-muted max-w-[640px] mx-auto leading-[1.6]">
          {lead}
        </p>
      )}
    </div>
  );
}

// Bordered surface card used for capability/feature tiles.
export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`p-5 sm:p-6 rounded ${className}`}
      style={{
        background: "rgb(var(--color-bg2))",
        border: "1px solid rgb(var(--color-stroke))",
      }}
    >
      {children}
    </div>
  );
}
